import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Gauge,
  TrendingUp,
  FileText,
  AlertTriangle,
  ArrowUpRight,
  Zap,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
} from 'recharts';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

export function SettingsUsagePage() {
  const usage = useQuery({
    queryKey: ['billing', 'usage'],
    queryFn: async () => (await api.get('/api/billing/usage')).data,
  });

  const subscription = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: async () => (await api.get('/api/billing/subscription')).data,
  });

  const byTypeData = useMemo(() => {
    if (!usage.data?.byEventType) return [];
    const labels: Record<string, { label: string; color: string }> = {
      document_ingest: { label: 'Document ingest', color: '#3b82f6' },
      'skill_regulatory-csr-extraction': { label: 'CSR extraction', color: '#6366f1' },
      'skill_cmc-analysis': { label: 'CMC analysis', color: '#8b5cf6' },
      'skill_cross-reference-check': { label: 'Cross-reference', color: '#a855f7' },
      'skill_gap-analysis': { label: 'Gap analysis', color: '#d946ef' },
      'skill_meddra-coding': { label: 'MedDRA coding', color: '#ec4899' },
      'skill_safety-narrative': { label: 'Safety narrative', color: '#f43f5e' },
      'skill_psmf-scan': { label: 'PSMF scan', color: '#14b8a6' },
      'skill_sop-scan': { label: 'SOP scan', color: '#06b6d4' },
    };
    return usage.data.byEventType.map((e: { eventType: string; pages: number; aiCostUsd: number; count: number }) => ({
      name: labels[e.eventType]?.label ?? e.eventType,
      color: labels[e.eventType]?.color ?? '#6b7280',
      pages: e.pages,
      cost: e.aiCostUsd,
      count: e.count,
    }));
  }, [usage.data]);

  const utilisation = (usage.data?.utilisation ?? 0) * 100;
  const overBudget = utilisation > 100;
  const nearBudget = utilisation >= 80 && utilisation <= 100;

  if (usage.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Usage & billing</h1>
        <p className="text-sm text-muted-foreground">
          Period {usage.data?.billingPeriod}. Pages metered per document upload + per AI skill call.
        </p>
      </div>

      {/* Alerts */}
      {overBudget && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <div className="flex-1 text-sm">
              <div className="font-medium text-destructive">Over plan quota</div>
              <div className="text-muted-foreground">
                You&apos;re {Math.round(utilisation - 100)}% over your included pages. Overage charged at
                plan rate. Consider upgrading.
              </div>
            </div>
            <Button variant="default" size="sm">Upgrade plan</Button>
          </CardContent>
        </Card>
      )}
      {nearBudget && !overBudget && (
        <Card className="border-amber-500/30 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div className="flex-1 text-sm">
              <div className="font-medium text-amber-900 dark:text-amber-300">Approaching plan quota</div>
              <div className="text-muted-foreground">{Math.round(utilisation)}% of monthly pages used.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Headline stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          icon={FileText}
          label="Pages used"
          value={usage.data?.pagesUsed?.toLocaleString() ?? '0'}
          sub={usage.data?.pagesIncluded ? `of ${usage.data.pagesIncluded.toLocaleString()}` : 'no plan limit'}
        />
        <StatCard
          icon={Gauge}
          label="Utilisation"
          value={usage.data?.pagesIncluded ? `${utilisation.toFixed(0)}%` : '—'}
          sub={usage.data?.pagesRemaining != null ? `${usage.data.pagesRemaining.toLocaleString()} remaining` : ''}
          tone={overBudget ? 'destructive' : nearBudget ? 'warning' : 'default'}
        />
        <StatCard
          icon={Zap}
          label="AI events"
          value={usage.data?.totalEvents?.toLocaleString() ?? '0'}
          sub="this period"
        />
      </div>

      {/* LLM spend cap circuit-breaker status */}
      {usage.data?.llmSpend && (
        <LlmSpendCard llmSpend={usage.data.llmSpend} />
      )}

      {/* Plan */}
      <Card>
        <CardHeader>
          <CardTitle>Current plan</CardTitle>
          <CardDescription>Change plan, view invoices, or open the billing portal.</CardDescription>
        </CardHeader>
        <CardContent>
          {subscription.data?.subscription ? (
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-xl">{subscription.data.subscription.plan.name}</span>
                  <Badge variant={subscription.data.subscription.status === 'active' ? 'success' : 'warning'}>
                    {subscription.data.subscription.status}
                  </Badge>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {subscription.data.subscription.currency} · {subscription.data.subscription.billingPeriod}
                  {subscription.data.subscription.currentPeriodEnd &&
                    ` ? renews ${new Date(subscription.data.subscription.currentPeriodEnd).toLocaleDateString()}`}
                  {subscription.data.subscription.cancelAtPeriodEnd && ' ? will cancel at period end'}
                </div>
                <ul className="mt-3 flex flex-wrap gap-1.5">
                  {subscription.data.subscription.plan.includedFeatures?.map((f: string) => (
                    <li key={f}>
                      <Badge variant="secondary">{f}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={async () => {
                  const res = await api.post('/api/billing/portal', { returnUrl: window.location.href });
                  window.location.href = res.data.portalUrl;
                }}>
                  <ArrowUpRight className="mr-1.5 h-3.5 w-3.5" /> Billing portal
                </Button>
                <Button>Change plan</Button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed bg-muted/20 p-6 text-center">
              <TrendingUp className="mx-auto h-8 w-8 text-muted-foreground" />
              <div className="mt-3 font-medium">No active subscription</div>
              <p className="mt-1 text-sm text-muted-foreground">
                You&apos;re on the free demo tier. Subscribe to unlock production usage + included page credits.
              </p>
              <Button className="mt-4">View plans</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Usage breakdown</CardTitle>
          <CardDescription>Pages + event counts per skill, this period.</CardDescription>
        </CardHeader>
        <CardContent>
          {byTypeData.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No usage yet this period. Upload a document or run an AI workflow to see events.
            </p>
          ) : (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byTypeData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={70} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        fontSize: '12px',
                      }}
                    />
                    <Bar dataKey="pages" name="Pages" radius={[4, 4, 0, 0]}>
                      {byTypeData.map((entry: { color: string }, i: number) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 grid gap-2 text-xs md:grid-cols-2">
                {byTypeData.map((e: { name: string; color: string; count: number; pages: number; cost: number }) => (
                  <div key={e.name} className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: e.color }} />
                      <span>{e.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <span>{e.count} calls</span>
                      <span>{e.pages} pages</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface LlmSpend {
  spendUSD: number;
  capUSD: number;
  utilisation: number;
  status: 'ok' | 'soft-cap-exceeded' | 'hard-cap-exceeded';
  callCount: number;
  tokensIn: number;
  tokensOut: number;
}

function LlmSpendCard({ llmSpend }: { llmSpend: LlmSpend }) {
  const pct = Math.min(100, Math.round(llmSpend.utilisation * 100));
  const colour =
    llmSpend.status === 'hard-cap-exceeded'
      ? 'bg-destructive'
      : llmSpend.status === 'soft-cap-exceeded'
      ? 'bg-amber-500'
      : 'bg-emerald-500';
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">LLM monthly spend</CardTitle>
        <CardDescription>
          Circuit-breaker status. Calls are refused with 402 once the hard cap is reached. Soft-cap
          notifications fire at the configured threshold.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline justify-between">
          <div>
            <span className="font-display text-xl">${llmSpend.spendUSD.toFixed(2)}</span>
            <span className="ml-2 text-sm text-muted-foreground">of ${llmSpend.capUSD.toFixed(0)} monthly cap</span>
          </div>
          <Badge
            variant={
              llmSpend.status === 'hard-cap-exceeded'
                ? 'destructive'
                : llmSpend.status === 'soft-cap-exceeded'
                ? 'warning'
                : 'success'
            }
          >
            {llmSpend.status.replace(/-/g, ' ')}
          </Badge>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className={`h-full ${colour} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <div>{pct}% used</div>
          <div>{llmSpend.callCount.toLocaleString()} calls this month</div>
          <div>{(llmSpend.tokensIn + llmSpend.tokensOut).toLocaleString()} tokens</div>
        </div>
        {llmSpend.status === 'hard-cap-exceeded' && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs">
            <strong className="text-destructive">Calls refused.</strong> All AI calls return HTTP 402 until the cap is
            raised or next month rolls over. Contact <a href="mailto:hello@praxara.io" className="underline">hello@praxara.io</a> if you need an emergency raise.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'default' | 'warning' | 'destructive';
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div
          className={`mt-4 font-display text-2xl ${
            tone === 'destructive' ? 'text-destructive' : tone === 'warning' ? 'text-amber-600' : ''
          }`}
        >
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {sub && <div className="mt-0.5 text-[10px] text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}
